import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { RootState } from '@/redux/store';
import { Routes } from '@/routes/router';
import { ensurePushSubscription, registerServiceWorker } from '@/api/webpushApi';

// Plays a short two-tone chime via Web Audio. Falls back silently if the
// browser blocks audio (e.g. no prior user gesture).
const playChime = () => {
    try {
        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.5);
    } catch {
        // Ignore — some browsers block until user interaction.
    }
};

export const useChatNotifications = () => {
    const { t } = useTranslation();
    const user = useSelector((state: RootState) => state.user.user);
    const navigate = useNavigate();
    const location = useLocation();

    const wsRef = useRef<WebSocket | null>(null);
    const titleIntervalRef = useRef<number | null>(null);
    const originalTitleRef = useRef<string>(typeof document !== 'undefined' ? document.title : '');
    const unreadCountRef = useRef<number>(0);
    const locationRef = useRef(location);

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    const stopTitleFlash = useCallback(() => {
        if (titleIntervalRef.current) {
            window.clearInterval(titleIntervalRef.current);
            titleIntervalRef.current = null;
        }
        document.title = originalTitleRef.current;
        unreadCountRef.current = 0;
    }, []);

    const startTitleFlash = useCallback(() => {
        if (titleIntervalRef.current) return;
        let toggle = true;
        titleIntervalRef.current = window.setInterval(() => {
            const count = unreadCountRef.current;
            document.title = toggle
                ? `(${count}) 💬 ${t('chat.notification.title', 'New chat message')}`
                : originalTitleRef.current;
            toggle = !toggle;
        }, 1000);
    }, [t]);

    // Register the service worker up-front so it's installed before the user
    // clicks Enable — and if permission is already granted, make sure our
    // push subscription exists on the backend.
    useEffect(() => {
        if (!user) return;
        registerServiceWorker();
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            ensurePushSubscription().catch((err) =>
                console.error('Failed to ensure push subscription:', err)
            );
        }
    }, [user]);

    // Stop the title flash as soon as the user looks at /chat with the tab visible.
    useEffect(() => {
        const maybeClear = () => {
            if (
                document.visibilityState === 'visible' &&
                locationRef.current.pathname === Routes.PORTAL_CHAT
            ) {
                stopTitleFlash();
            }
        };
        document.addEventListener('visibilitychange', maybeClear);
        window.addEventListener('focus', maybeClear);
        return () => {
            document.removeEventListener('visibilitychange', maybeClear);
            window.removeEventListener('focus', maybeClear);
        };
    }, [stopTitleFlash]);

    useEffect(() => {
        if (location.pathname === Routes.PORTAL_CHAT && document.visibilityState === 'visible') {
            stopTitleFlash();
        }
    }, [location.pathname, stopTitleFlash]);

    // Notifications WebSocket — persistent while the user is logged in.
    useEffect(() => {
        if (!user) {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            return;
        }

        let stopped = false;
        let retryTimer: number | null = null;

        const connect = () => {
            if (stopped) return;

            try {
                const baseURL = import.meta.env.VITE_PLAYER_HUB_URL || 'https://player-hub-stage.balancegolf.com';
                const wsProtocol = baseURL.startsWith('https://') ? 'wss://' : 'ws://';
                const wsBaseURL = baseURL.replace(/^https?:\/\//, '');
                const token = localStorage.getItem('jwt_token');
                const wsUrl = `${wsProtocol}${wsBaseURL}/notifications/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;

                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data?.type !== 'chat') return;

                        const title: string = data.title || t('chat.notification.title', 'New chat message');
                        const body: string = data.body || '';
                        const chatId: string | undefined = data.chatId;

                        // Refresh the header unread badge wherever the user is.
                        window.dispatchEvent(new Event('chatUpdated'));

                        // If the user is already on the chat page and actively looking at
                        // the tab, don't interrupt — the in-chat WebSocket handles the update.
                        const onChatPage = locationRef.current.pathname === Routes.PORTAL_CHAT;
                        const tabVisible = document.visibilityState === 'visible';
                        if (onChatPage && tabVisible) return;

                        playChime();
                        unreadCountRef.current += 1;
                        startTitleFlash();

                        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                            const notification = new Notification(title, {
                                body,
                                tag: chatId ? `chat-${chatId}` : undefined,
                                icon: '/android-chrome-192x192.png',
                            });
                            notification.onclick = () => {
                                window.focus();
                                navigate(Routes.PORTAL_CHAT + (chatId ? `?chatId=${chatId}` : ''));
                                notification.close();
                            };
                        } else if (body) {
                            toast.info(`${title}: ${body}`);
                        }
                    } catch (err) {
                        console.error('Error parsing notification WS message:', err, event.data);
                    }
                };

                ws.onerror = (err) => {
                    console.error('Notifications WS error:', err);
                };

                ws.onclose = () => {
                    wsRef.current = null;
                    if (!stopped) {
                        retryTimer = window.setTimeout(connect, 3000);
                    }
                };
            } catch (err) {
                console.error('Error opening notifications WS:', err);
            }
        };

        connect();

        return () => {
            stopped = true;
            if (retryTimer) window.clearTimeout(retryTimer);
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [user, t, navigate, startTitleFlash]);
};

export default useChatNotifications;
